import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent, Button, Spinner } from '../../../shared/components/ui';
import { ConfirmDialog } from '../../../shared/components/Dialogs/ConfirmDialog';
import { settingsApi } from '../../../shared/services/settingsApi';

/**
 * Tab button component
 */
function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
        active
          ? 'border-blue-500 text-blue-600 dark:text-blue-400'
          : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-100 hover:border-gray-300'
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Similarity metric item component (editable)
 */
function MetricItem({
  metric,
  weight,
  index,
  onWeightChange,
  isEditing,
  onEditStart,
  onEditEnd,
}) {
  const inputRef = useRef(null);
  const [localValue, setLocalValue] = useState(weight.toString());

  useEffect(() => {
    if (!isEditing) {
      setLocalValue(weight.toString());
    }
  }, [weight, isEditing]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleInputChange = (e) => {
    setLocalValue(e.target.value);
  };

  const handleInputBlur = () => {
    const numValue = parseFloat(localValue);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      onWeightChange(metric.id, numValue);
    } else {
      setLocalValue(weight.toString());
    }
    onEditEnd();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleInputBlur();
    } else if (e.key === 'Escape') {
      setLocalValue(weight.toString());
      onEditEnd();
    }
  };

  return (
    <div className="flex items-center p-4 bg-white dark:bg-gray-900 border rounded-lg border-gray-200 dark:border-gray-800 hover:border-blue-300 hover:shadow-sm transition-all duration-300">
      <span className="flex items-center justify-center w-8 h-8 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full text-sm font-medium mr-4 flex-shrink-0">
        {index + 1}
      </span>

      <div className="flex-1 min-w-0 mr-4">
        <div className="font-medium text-gray-900 dark:text-gray-100">{metric.displayName}</div>
        <div className="text-sm text-gray-500 dark:text-gray-400 truncate">{metric.description}</div>
      </div>

      <div className="flex-shrink-0 ml-auto">
        {isEditing ? (
          <div className="flex items-center">
            <input
              ref={inputRef}
              type="number"
              min="0"
              max="100"
              step="1"
              value={localValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onKeyDown={handleKeyDown}
              className="w-16 px-2 py-1 text-right border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="ml-1 text-gray-600 dark:text-gray-300">%</span>
          </div>
        ) : (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEditStart(metric.id);
            }}
            className="px-3 py-1 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-full text-sm font-medium transition-colors"
          >
            {weight}%
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Pie chart visualization for weight distribution
 */
function PieChart({ metrics, weights }) {
  const orderedMetrics = [...metrics].sort((a, b) => (weights[b.id] || 0) - (weights[a.id] || 0));

  const colors = [
    '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
    '#8B5CF6', '#EC4899', '#14B8A6', '#F97316',
  ];

  let currentAngle = -90;
  const slices = orderedMetrics.map((metric, index) => {
    const percentage = weights[metric.id] || 0;
    const angle = (percentage / 100) * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;

    return { metric, percentage, startAngle, endAngle, color: colors[index % colors.length] };
  });

  const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
    const angleInRadians = (angleInDegrees * Math.PI) / 180.0;
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians),
    };
  };

  const createArc = (startAngle, endAngle) => {
    const centerX = 100, centerY = 100, radius = 80;
    const start = polarToCartesian(centerX, centerY, radius, endAngle);
    const end = polarToCartesian(centerX, centerY, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

    return [
      `M ${centerX} ${centerY}`,
      `L ${start.x} ${start.y}`,
      `A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`,
      'Z',
    ].join(' ');
  };

  return (
    <div className="flex flex-col items-center">
      <svg viewBox="0 0 200 200" className="w-48 h-48">
        {slices.map((slice) => (
          <path
            key={slice.metric.id}
            d={createArc(slice.startAngle, slice.endAngle)}
            fill={slice.color}
            stroke="white"
            strokeWidth="2"
            className="transition-all duration-300"
          />
        ))}
      </svg>

      <div className="mt-4 grid grid-cols-2 gap-2 w-full max-w-sm">
        {orderedMetrics.map((metric, index) => (
          <div key={metric.id} className="flex items-center text-xs">
            <div
              className="w-3 h-3 rounded-sm mr-2 flex-shrink-0"
              style={{ backgroundColor: colors[index % colors.length] }}
            />
            <span className="text-gray-700 dark:text-gray-200 truncate flex-1" title={metric.displayName}>
              {metric.displayName}
            </span>
            <span className="font-medium text-gray-900 dark:text-gray-100 ml-1">
              {weights[metric.id] || 0}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Division accordion item for field weights
 */
function DivisionAccordion({ divisionKey, division, fieldWeights, onFieldWeightChange, isExpanded, onToggle }) {
  const hasOverrides = Object.keys(fieldWeights).some(
    (fieldName) => fieldWeights[fieldName] !== undefined
  );

  return (
    <div className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        <div className="flex items-center">
          <span className="font-mono text-sm bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded mr-3">
            {divisionKey}
          </span>
          <span className="font-medium text-gray-900 dark:text-gray-100">{division.title}</span>
          {hasOverrides && (
            <span className="ml-2 text-xs bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded">
              Customized
            </span>
          )}
        </div>
        <div className="flex items-center text-gray-500 dark:text-gray-400">
          <span className="text-sm mr-2">{division.fields.length} fields</span>
          <svg
            className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {isExpanded && (
        <div className="p-4 space-y-3 bg-white dark:bg-gray-900">
          {division.fields.map((field) => {
            const currentWeight = fieldWeights[field.fieldName] !== undefined
              ? fieldWeights[field.fieldName]
              : field.importanceWeight;
            const isOverridden = fieldWeights[field.fieldName] !== undefined;
            const displayPercentage = Math.round(currentWeight * 100);

            return (
              <div key={field.fieldName} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-800 last:border-0">
                <div className="flex-1 min-w-0 mr-4">
                  <div className="flex items-center">
                    <span className="font-medium text-gray-800 dark:text-gray-200 text-sm">{field.displayName}</span>
                    {isOverridden && (
                      <button
                        onClick={() => onFieldWeightChange(divisionKey, field.fieldName, undefined)}
                        className="ml-2 text-xs text-gray-400 dark:text-gray-500 hover:text-red-500"
                        title="Reset to default"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                  {field.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{field.description}</p>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={displayPercentage}
                    onChange={(e) => onFieldWeightChange(divisionKey, field.fieldName, parseInt(e.target.value) / 100)}
                    className="w-24 h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                  />
                  <span className={`text-sm font-medium w-12 text-right ${isOverridden ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-300'}`}>
                    {displayPercentage}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Similarity Weights Tab Content
 */
function SimilarityWeightsTab({
  metrics,
  weights,
  onWeightChange,
  editingMetricId,
  setEditingMetricId,
}) {
  const sortedMetrics = [...metrics].sort((a, b) => (weights[b.id] || 0) - (weights[a.id] || 0));
  const weightTotal = Object.values(weights).reduce((sum, w) => sum + (w || 0), 0);
  const isTotalValid = Math.abs(weightTotal - 100) < 0.5;

  return (
    <div className="space-y-6">
      {!isTotalValid && (
        <div className="p-4 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-800 dark:text-amber-300 flex items-center">
          <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <div>
            <strong>Total must equal 100%</strong>
            <span className="ml-2">Current total: {weightTotal}%</span>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Similarity Metrics</CardTitle>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Click percentages to edit. Controls how AI finds similar historical projects.
              </p>
            </div>
            <div className={`text-sm font-medium ${isTotalValid ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              Total: {weightTotal}%
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {sortedMetrics.map((metric, index) => (
              <MetricItem
                key={metric.id}
                metric={metric}
                weight={weights[metric.id] || 0}
                index={index}
                onWeightChange={onWeightChange}
                isEditing={editingMetricId === metric.id}
                onEditStart={setEditingMetricId}
                onEditEnd={() => setEditingMetricId(null)}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-gray-50 dark:bg-gray-900">
        <CardHeader>
          <CardTitle className="text-base">Weight Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <PieChart metrics={metrics} weights={weights} />
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Division Weights Tab Content
 */
function DivisionWeightsTab({ vocabulary, weights, onFieldWeightChange }) {
  const [expandedDivision, setExpandedDivision] = useState(null);

  const divisionKeys = Object.keys(vocabulary || {}).sort();
  const hasAnyOverrides = Object.keys(weights || {}).length > 0;

  return (
    <div className="space-y-6">
      <Card className="bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800">
        <CardContent className="py-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div>
              <h4 className="text-sm font-medium text-blue-900 dark:text-blue-200">Division Field Importance</h4>
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                Adjust the importance of specific construction fields when matching similar projects.
                Fields with higher importance have more influence on similarity scores.
                Changes only affect your account.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {hasAnyOverrides && (
        <div className="flex items-center justify-between px-1">
          <span className="text-sm text-gray-600 dark:text-gray-300">
            You have customized {Object.keys(weights).reduce((count, div) => count + Object.keys(weights[div]).length, 0)} field weights
          </span>
        </div>
      )}

      <div className="space-y-3">
        {divisionKeys.map((divKey) => (
          <DivisionAccordion
            key={divKey}
            divisionKey={divKey}
            division={vocabulary[divKey]}
            fieldWeights={weights[divKey] || {}}
            onFieldWeightChange={onFieldWeightChange}
            isExpanded={expandedDivision === divKey}
            onToggle={() => setExpandedDivision(expandedDivision === divKey ? null : divKey)}
          />
        ))}
      </div>

      {divisionKeys.length === 0 && (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <p>No division vocabulary available.</p>
          <p className="text-sm mt-1">Division field weights will appear here once vocabulary is configured.</p>
        </div>
      )}
    </div>
  );
}

/**
 * Line Item Mappings Tab Content
 *
 * Lists every per-tenant description -> MasterFormat code mapping that the
 * user has saved via the Reclassify or Reassign Code flows. Each row can be
 * deleted; deletions are immediate (no global save flow).
 */
function LineItemMappingsTab() {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await settingsApi.listLineItemMappings();
      setMappings(data.mappings || []);
    } catch (err) {
      console.error('Error loading line item mappings:', err);
      setError('Failed to load saved mappings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = mappings.filter((m) => {
    if (!filter) return true;
    const needle = filter.toLowerCase();
    return (
      (m.originalDescription || '').toLowerCase().includes(needle)
      || (m.masterformatCode || '').toLowerCase().includes(needle)
      || (m.masterformatTitle || '').toLowerCase().includes(needle)
    );
  });

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      setDeleting(true);
      await settingsApi.deleteLineItemMapping(pendingDelete.normalizedDescription);
      setMappings((prev) => prev.filter(
        (m) => m.normalizedDescription !== pendingDelete.normalizedDescription,
      ));
      setPendingDelete(null);
    } catch (err) {
      console.error('Failed to delete mapping:', err);
      setError('Failed to delete mapping. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800">
        <CardContent className="py-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div>
              <h4 className="text-sm font-medium text-blue-900 dark:text-blue-200">Saved Line Item Mappings</h4>
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                Each time you Reclassify a line item or Reassign an unclassified item with
                "Remember this mapping" checked, the description -&gt; MasterFormat code mapping
                is saved here. Future estimate uploads use these mappings before the AI runs.
                Delete a mapping to let the AI classify that description again.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle>Mappings</CardTitle>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {mappings.length} total{filter ? ` · showing ${filtered.length}` : ''}
              </p>
            </div>
            <input
              type="search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by description or code..."
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-700 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
            />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : mappings.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <p className="text-sm">No saved mappings yet.</p>
              <p className="text-xs mt-1">
                Saved mappings appear here as you reclassify line items during estimate review.
              </p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
              No mappings match "{filter}".
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-800 text-left text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide">
                    <th className="py-2 pr-4 font-medium">Description</th>
                    <th className="py-2 pr-4 font-medium w-32">Code</th>
                    <th className="py-2 pr-4 font-medium">MasterFormat Title</th>
                    <th className="py-2 pr-4 font-medium w-32">Saved</th>
                    <th className="py-2 font-medium w-24 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((m) => (
                    <tr key={m.normalizedDescription} className="border-b border-gray-100 dark:border-gray-800 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="py-2 pr-4 text-gray-900 dark:text-gray-100">
                        {m.originalDescription || m.normalizedDescription}
                      </td>
                      <td className="py-2 pr-4 font-mono text-xs text-gray-700 dark:text-gray-200">
                        {m.masterformatCode}
                      </td>
                      <td className="py-2 pr-4 text-gray-600 dark:text-gray-300">
                        {m.masterformatTitle || <span className="text-gray-400 dark:text-gray-500">—</span>}
                      </td>
                      <td className="py-2 pr-4 text-xs text-gray-500 dark:text-gray-400">
                        {m.updatedAt
                          ? new Date(m.updatedAt).toLocaleDateString()
                          : '—'}
                      </td>
                      <td className="py-2 text-right">
                        <button
                          type="button"
                          onClick={() => setPendingDelete(m)}
                          className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 font-medium"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        isOpen={!!pendingDelete}
        title="Delete saved mapping?"
        message={
          pendingDelete
            ? `Future uploads containing "${pendingDelete.originalDescription || pendingDelete.normalizedDescription}" will be classified by the AI again instead of using ${pendingDelete.masterformatCode}.`
            : ''
        }
        confirmLabel={deleting ? 'Deleting...' : 'Delete'}
        cancelLabel="Cancel"
        confirmVariant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}

/**
 * PricingRules page component
 * Allows users to customize similarity weights and division field importance
 */
export function PricingRules() {
  const [activeTab, setActiveTab] = useState('similarity');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Similarity weights state
  const [similarityMetrics, setSimilarityMetrics] = useState([]);
  const [similarityWeights, setSimilarityWeights] = useState({});
  const [originalSimilarityWeights, setOriginalSimilarityWeights] = useState({});
  const [editingMetricId, setEditingMetricId] = useState(null);

  // Division weights state
  const [vocabulary, setVocabulary] = useState({});
  const [divisionWeights, setDivisionWeights] = useState({});
  const [originalDivisionWeights, setOriginalDivisionWeights] = useState({});

  const hasSimilarityChanges = JSON.stringify(similarityWeights) !== JSON.stringify(originalSimilarityWeights);
  const hasDivisionChanges = JSON.stringify(divisionWeights) !== JSON.stringify(originalDivisionWeights);
  const hasChanges = hasSimilarityChanges || hasDivisionChanges;

  // Load pricing rules on mount
  useEffect(() => {
    loadPricingRules();
  }, []);

  const loadPricingRules = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await settingsApi.getPricingRules();

      // Similarity weights
      setSimilarityMetrics(data.similarityWeights?.metrics || []);
      setSimilarityWeights(data.similarityWeights?.weights || {});
      setOriginalSimilarityWeights(data.similarityWeights?.weights || {});

      // Division weights
      setVocabulary(data.divisionWeights?.vocabulary || {});
      setDivisionWeights(data.divisionWeights?.weights || {});
      setOriginalDivisionWeights(data.divisionWeights?.weights || {});
    } catch (err) {
      setError('Failed to load pricing rules. Please try again.');
      console.error('Error loading pricing rules:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSimilarityWeightChange = useCallback((metricId, newValue) => {
    setSimilarityWeights(prev => ({
      ...prev,
      [metricId]: newValue,
    }));
    setSuccess(false);
  }, []);

  const handleDivisionWeightChange = useCallback((divisionKey, fieldName, newValue) => {
    setDivisionWeights(prev => {
      const newWeights = { ...prev };

      if (newValue === undefined) {
        // Remove override
        if (newWeights[divisionKey]) {
          const { [fieldName]: _, ...rest } = newWeights[divisionKey];
          if (Object.keys(rest).length === 0) {
            delete newWeights[divisionKey];
          } else {
            newWeights[divisionKey] = rest;
          }
        }
      } else {
        // Set override
        newWeights[divisionKey] = {
          ...(newWeights[divisionKey] || {}),
          [fieldName]: newValue,
        };
      }

      return newWeights;
    });
    setSuccess(false);
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const updateData = {};
      if (hasSimilarityChanges) {
        updateData.similarityWeights = similarityWeights;
      }
      if (hasDivisionChanges) {
        updateData.divisionWeights = divisionWeights;
      }

      await settingsApi.updatePricingRules(updateData);

      setOriginalSimilarityWeights(similarityWeights);
      setOriginalDivisionWeights(divisionWeights);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Failed to save changes. Please try again.');
      console.error('Error saving pricing rules:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      setSaving(true);
      setError(null);
      const data = await settingsApi.resetPricingRules();

      setSimilarityWeights(data.similarityWeights?.weights || {});
      setOriginalSimilarityWeights(data.similarityWeights?.weights || {});
      setDivisionWeights(data.divisionWeights?.weights || {});
      setOriginalDivisionWeights(data.divisionWeights?.weights || {});

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Failed to reset to defaults. Please try again.');
      console.error('Error resetting pricing rules:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setSimilarityWeights(originalSimilarityWeights);
    setDivisionWeights(originalDivisionWeights);
    setSuccess(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const weightTotal = Object.values(similarityWeights).reduce((sum, w) => sum + (w || 0), 0);
  const isTotalValid = Math.abs(weightTotal - 100) < 0.5;
  const canSave = hasChanges && (activeTab !== 'similarity' || isTotalValid);

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Pricing Rules</h1>
        <p className="text-gray-600 dark:text-gray-300 mt-1">
          Customize how AI matches historical projects and weighs construction details.
        </p>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 flex items-center">
          <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          {error}
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300 flex items-center">
          <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          Settings saved successfully!
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-800 mb-6">
        <nav className="-mb-px flex space-x-8">
          <TabButton
            active={activeTab === 'similarity'}
            onClick={() => setActiveTab('similarity')}
          >
            Similarity Weights
            {hasSimilarityChanges && <span className="ml-2 text-amber-500">*</span>}
          </TabButton>
          <TabButton
            active={activeTab === 'division'}
            onClick={() => setActiveTab('division')}
          >
            Division Field Weights
            {hasDivisionChanges && <span className="ml-2 text-amber-500">*</span>}
          </TabButton>
          <TabButton
            active={activeTab === 'mappings'}
            onClick={() => setActiveTab('mappings')}
          >
            Line Item Mappings
          </TabButton>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'similarity' && (
        <SimilarityWeightsTab
          metrics={similarityMetrics}
          weights={similarityWeights}
          onWeightChange={handleSimilarityWeightChange}
          editingMetricId={editingMetricId}
          setEditingMetricId={setEditingMetricId}
        />
      )}

      {activeTab === 'division' && (
        <DivisionWeightsTab
          vocabulary={vocabulary}
          weights={divisionWeights}
          onFieldWeightChange={handleDivisionWeightChange}
        />
      )}

      {activeTab === 'mappings' && <LineItemMappingsTab />}

      {/* Actions — only relevant for the weight-editing tabs, not the
          mappings tab where each row delete is its own atomic action. */}
      {activeTab !== 'mappings' && (
        <div className="mt-8 flex items-center justify-between border-t border-gray-200 dark:border-gray-800 pt-6">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {hasChanges && !canSave && activeTab === 'similarity' && 'Adjust weights to reach 100% total'}
            {hasChanges && canSave && 'You have unsaved changes'}
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={handleReset}
              disabled={saving}
            >
              Reset to Defaults
            </Button>
            <Button
              variant="outline"
              onClick={handleDiscard}
              disabled={!hasChanges || saving}
            >
              Discard Changes
            </Button>
            <Button
              onClick={handleSave}
              disabled={!canSave || saving}
              loading={saving}
            >
              Save Changes
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default PricingRules;
